<template>
  <section class="w-full">
    <h1 class="flex items-center justify-between">
      <div>
        {{ $t('components.landingpages.title') }}
        <Feature
          v-slot="{feature}"
          :feature-slug="featureNames.VARIANT"
        >
          <span
            v-show="company && hasMultipleVariants(feature.config.variants)"
            class="ml-2 text-base text-gray-600"
          >{{ company.variant }}</span>
        </Feature>
      </div>
      <div class="flex items-center">
        <button
          class="btn-inline-small"
          @click="showExportImplementationData()"
        >
          {{ $t('components.landingpages.implementationData') }}
        </button>

        <span class="mx-3 text-sm font-bold text-gray-600">|</span>
        <button
          class="btn-inline-small"
          @click="showCustomUrlsModal()"
        >
          {{ $t('components.landingpages.customUrls') }}
        </button>
        <Feature
          v-slot="{feature}"
          :feature-slug="featureNames.VARIANT"
        >
          <div v-show="company && hasMultipleVariants(feature.config.variants)">
            <span class="mx-3 text-sm font-bold text-gray-600">|</span>
            <button
              class="btn-inline-small"
              @click="showVariantUpdateModal()"
            >
              {{ $t('components.landingpages.changeVariant') }}
            </button>
          </div>
        </Feature>

        <span
          v-if="hasRole(ROLES.SUPER_ADMIN)"
          class="mx-3 text-sm font-bold text-gray-600"
        >|</span>
        <button
          v-if="hasRole(ROLES.SUPER_ADMIN)"
          class="btn-inline-small"
          @click="showLandingpageBulkCreateModal()"
        >
          {{ $t('modals.landingpage.bulkCreateTitle') }}
        </button>

        <span
          v-if="hasRole(ROLES.SUPER_ADMIN)"
          class="mx-3 text-sm font-bold text-gray-600"
        >|</span>
        <button
          v-if="hasRole(ROLES.SUPER_ADMIN)"
          class="btn-inline-small"
          @click="showGenerateModal()"
        >
          {{ $t('components.landingpages.generateMaterials') }}
        </button>
      </div>
    </h1>
    <div class="w-full">
      <div
        v-if="isProvisioning"
        class="flex items-center p-3 mb-4 text-sm text-blue-800 bg-blue-100 border border-blue-300 rounded"
      >
        <fa
          :icon="faSync"
          class="mr-2 animate-spin"
          fixed-width
        />
        {{ $t('components.landingpages.provisioningMaterials') }}
      </div>
      <div
        v-else-if="isCurrentlyGenerating"
        class="flex items-center p-3 mb-4 text-sm text-blue-800 bg-blue-100 border border-blue-300 rounded"
      >
        <fa
          :icon="faSync"
          class="mr-2 animate-spin"
          fixed-width
        />
        {{ $t('components.landingpages.generatingMaterials') }}
      </div>
      <div
        v-if="company.productType === 'BASIC'"
        class="pb-4"
      >
        <span class="pb-4 text-lg font-semibold text-gray-700">
          {{ $t('components.landingpages.bookedLandingpages') }}
        </span>
        {{ company.bookedLandingpageSlugs.join(', ') }}
      </div>
      <h3 class="text-lg font-semibold text-gray-700">
        {{ $t('components.landingpages.tools') }}
      </h3>
      <ul>
        <li
          v-for="landingpage in tools"
          :key="landingpage.slug"
          class="w-full py-2"
        >
          <landingpage
            :landingpage="landingpage"
            :campaigns="campaigns"
            :company="company"
            :materials="getMaterialsForLandingpage(landingpage.id)"
            :is-provisioning="isLandingpageProvisioning(landingpage.id)"
          />
        </li>
      </ul>

      <h3 class="mt-6 text-lg font-semibold text-gray-700">
        {{ $t('pages.landingpages.form.types.GUIDE') }}
      </h3>
      <ul>
        <li
          v-for="landingpage in guides"
          :key="landingpage.slug"
          class="w-full py-2"
        >
          <landingpage
            :landingpage="landingpage"
            :campaigns="campaigns"
            :company="company"
            :materials="getMaterialsForLandingpage(landingpage.id)"
            :is-provisioning="isLandingpageProvisioning(landingpage.id)"
          />
        </li>
      </ul>

      <h3 class="mt-6 text-lg font-semibold text-gray-700">
        {{ $t('pages.landingpages.form.types.CHECKLIST') }}
      </h3>
      <ul>
        <li
          v-for="landingpage in checklists"
          :key="landingpage.slug"
          class="w-full py-2"
        >
          <landingpage
            :landingpage="landingpage"
            :campaigns="campaigns"
            :company="company"
            :materials="getMaterialsForLandingpage(landingpage.id)"
            :is-provisioning="isLandingpageProvisioning(landingpage.id)"
          />
        </li>
      </ul>

      <h3 class="mt-6 mb-3 text-lg font-semibold text-gray-700">
        {{ $t('components.landingpages.campaignsWithoutLandingpage') }}
      </h3>
      <div
        v-if="campaigns"
        class="relative text-gray-800 border rounded hover:shadow"
      >
        <div
          v-for="[campaignId, campignMaterials] in materialsByCampaign"
          :key="campaignId"
        >
          <h4 class="pl-4 mt-2 text-xs text-gray-600 uppercase">
            {{ campaigns.find(({id}) => id === campaignId)?.name }}
          </h4>
          <ul>
            <material-list-item
              v-for="material in campignMaterials"
              :key="material.id"
              :material="material"
              :company-id="company.id"
            />
          </ul>
        </div>

        <div
          v-for="set in materialSetsWithoutLandingpage"
          :key="set.materialSetId"
        >
          <ul>
            <material-set-list-item
              :material-set-id="set.materialSetId"
              :variants="set.variants"
              :company-id="company.id"
            />
          </ul>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import {
  faCode,
  faTrash,
  faFolder,
  faSync,
  faExternalLinkAlt
} from '@fortawesome/free-solid-svg-icons'
import UpdateVariantModal from '@/components/modals/company/variant/update'
import CustomUrlsModal from '@/components/modals/company/customUrls/edit'
import ExportImplementationDataModal from '@/components/modals/company/export-implementation/export-implementation'
import BulkCreateLandingpageModal from '@/components/modals/landingpage/bulk-create'
import GenerateModal from '@/components/modals/landingpage/generate'
import role from '@/mixins/role'
import groupBy from '@/lib/groupBy'
import Landingpage from './landingpage'
import materialListItem from './material-list-item'
import materialSetListItem from './material-set-list-item'
import MATERIALS from '@/graphql/material/materials.gql'
import LIST_CAMPAIGNS from '@/graphql/ListCampaigns.gql'
import featureMixin from '@/mixins/feature'
import variantMixin from '@/mixins/variant'
import eventBus, { eventNames } from '@/lib/eventBus'
import {
  isActiveCompany,
  isLandingpageProvisioning,
  isNonTerminal,
  observationReason
} from '@/lib/materialObservation'

const STATUS_UPDATE_INTERVAL_MS = 10000

/**
 * How often to re-read an ACTIVE company that currently looks idle.
 *
 * Generation is driven by the server — the Material Maker sweep, render
 * callbacks, a retry issued from another session. None of those tell this
 * screen anything, so watching only while *this* component believes work is in
 * flight means a user sits in front of a frozen tab and has to reload to
 * discover their materials finished. Deliberately much slower than the active
 * cadence: it exists to notice that work has begun, after which
 * `observationReason` takes over at full speed.
 */
const IDLE_STATUS_INTERVAL_MS = 30000

/** How often the observation decision is re-evaluated against the clock. */
const OBSERVATION_TICK_MS = 5000

export default {
  components: {
    Landingpage,
    materialListItem,
    materialSetListItem
  },

  mixins: [role, featureMixin, variantMixin],

  props: {
    company: { type: Object, default: null }
  },

  data () {
    return {
      materials: [],
      // Wall-clock start of the current observation window. Reset whenever work
      // is (re)started, which is what makes retry, regenerate, approval, and
      // reactivation resume watching instead of staying stopped.
      observationStartedAt: Date.now(),
      observationElapsedMs: 0,
      observationTicker: null,
      // The interval currently registered with Apollo, or `null`. Tracked as a
      // value rather than a boolean so the cadence can change between idle and
      // active without stacking two poll timers on one query.
      pollingIntervalMs: null,
      isDocumentHidden: typeof document !== 'undefined' && document.hidden,
      faCode,
      faTrash,
      faFolder,
      faSync,
      faExternalLinkAlt
    }
  },

  computed: {
    tools () {
      return this.company.landingpages.filter(lp =>
        ['VALUATION', 'RETURN_CALL', 'LIFE_ANNUITY', 'PARTIAL_SALE', 'PLOT_VALUATION', 'QUIZZARD', 'PROPERTY_REQUEST', 'NEWSLETTER'].includes(lp.type)
      )
    },

    guides () {
      return this.company.landingpages.filter(lp => lp.type === 'GUIDE')
    },

    checklists () {
      return this.company.landingpages.filter(lp => lp.type === 'CHECKLIST')
    },

    materialsByCampaign () {
      const materials = this.materials.filter(({ campaignId, landingpageId, materialMakerSetId }) => (
        campaignId && !landingpageId && !materialMakerSetId
      ))
      return Object.entries(groupBy(materials, 'campaignId'))
    },

    // Set members are never listed on their own; see material-set-list-item.
    materialSetsWithoutLandingpage () {
      const members = this.materials.filter(({ landingpageId, materialMakerSetId }) => (
        materialMakerSetId && !landingpageId
      ))
      return Object.entries(groupBy(members, 'materialMakerSetId')).map(([materialSetId, variants]) => ({
        materialSetId,
        variants: [...variants].sort((left, right) => (
          (left.materialMakerVariantIndex || 0) - (right.materialMakerVariantIndex || 0)
        ))
      }))
    },

    /**
     * Why materials are still being watched, or `null`.
     *
     * Derived from the rendered `materials` rather than from a second query, so
     * what the user sees and what drives polling can never disagree.
     */
    observationReason () {
      return observationReason({
        company: this.company,
        materials: this.materials,
        landingpages: this.company?.landingpages || [],
        elapsedMs: this.observationElapsedMs
      })
    },

    isProvisioning () {
      return this.observationReason === 'PROVISIONING'
    },

    isCurrentlyGenerating () {
      return this.observationReason === 'GENERATING'
    },

    /**
     * Whether any row is being worked on right now, ignoring the time window.
     *
     * Separate from `observationReason` on purpose: that answer expires with
     * the observation window, and work which starts after it has expired still
     * has to be picked up.
     */
    hasActiveWork () {
      return (this.materials || []).some(isNonTerminal)
    }
  },

  apollo: {
    materials: {
      query: MATERIALS,
      variables () {
        return {
          companyId: this.company.id
        }
      },
      skip () {
        return !this.company?.id
      },
      // The tab body is rendered behind `v-if`, so entering the Landing Pages
      // tab remounts this component. Under the default cache-first policy that
      // remount served whatever the cache held and issued no request, which is
      // why the tab could show stale rows indefinitely.
      fetchPolicy: 'cache-and-network'
    },

    campaigns: {
      query: LIST_CAMPAIGNS
    }
  },

  watch: {
    observationReason: {
      immediate: true,
      handler () {
        // Bound to the reason rather than to a raw status list: the moment
        // there is nothing left to watch, the fast cadence stops. This is the
        // bounded shutdown the previous unconditional `pollInterval` never had.
        this.syncPolling()
      }
    },

    hasActiveWork (isWorking, wasWorking) {
      // Work that began outside this screen arrives through the idle refresh.
      // Restarting the window on that edge is what lets the active cadence
      // engage even when the previous window had already expired — without it,
      // a tab left open longer than the observation limit could never resume.
      if (isWorking && !wasWorking) this.restartObservation()
    }
  },

  created () {
    this.$emit('refetch-company')
    this.restartObservation()
    // Retry, regenerate, approval, and unpublish all move materials back to a
    // non-terminal state. Without this, observation stayed stopped and the user
    // watched a frozen screen until they reloaded the page.
    eventBus.$on(eventNames.MATERIAL_WORK_STARTED, this.onMaterialWorkStarted)
    eventBus.$on(eventNames.COMPANY_STATE_UPDATED, this.onCompanyStateUpdated)
    document.addEventListener('visibilitychange', this.onVisibilityChange)
  },

  beforeDestroy () {
    this.stopPolling()
    this.clearObservationTicker()
    eventBus.$off(eventNames.MATERIAL_WORK_STARTED, this.onMaterialWorkStarted)
    eventBus.$off(eventNames.COMPANY_STATE_UPDATED, this.onCompanyStateUpdated)
    document.removeEventListener('visibilitychange', this.onVisibilityChange)
  },

  methods: {
    getMaterialsForLandingpage (landingpageId) {
      return this.materials.filter(material => material.landingpageId === landingpageId)
    },

    /** Whether one landing page's rows are still being created or generated. */
    isLandingpageProvisioning (landingpageId) {
      return isLandingpageProvisioning({
        company: this.company,
        landingpageMaterials: this.getMaterialsForLandingpage(landingpageId),
        elapsedMs: this.observationElapsedMs
      })
    },

    /** Begin a fresh observation window and re-read the server immediately. */
    restartObservation () {
      this.observationStartedAt = Date.now()
      this.observationElapsedMs = 0
      this.clearObservationTicker()
      // The elapsed clock is data, not a getter, so the bounded fallbacks
      // actually expire while the component sits open.
      this.observationTicker = setInterval(() => {
        this.observationElapsedMs = Date.now() - this.observationStartedAt
      }, OBSERVATION_TICK_MS)
      this.refetchMaterials()
    },

    clearObservationTicker () {
      if (!this.observationTicker) return
      clearInterval(this.observationTicker)
      this.observationTicker = null
    },

    /**
     * Apply the cadence the current state calls for.
     *
     * Three states, in precedence order: something is actively being watched,
     * the company is ACTIVE but idle, or there is nothing to watch at all. A
     * hidden tab polls at none of them.
     */
    syncPolling () {
      if (this.isDocumentHidden) {
        this.stopPolling()
        return
      }
      if (this.observationReason) {
        this.startPolling(STATUS_UPDATE_INTERVAL_MS)
        return
      }
      if (isActiveCompany(this.company)) {
        this.startPolling(IDLE_STATUS_INTERVAL_MS)
        return
      }
      this.stopPolling()
    },

    startPolling (intervalMs) {
      const query = this.$apollo.queries.materials
      if (!query || this.pollingIntervalMs === intervalMs) return
      // Apollo does not replace an existing interval, so switching cadence has
      // to stop the old timer first or both keep firing.
      if (this.pollingIntervalMs) query.stopPolling()
      query.startPolling(intervalMs)
      this.pollingIntervalMs = intervalMs
    },

    stopPolling () {
      const query = this.$apollo.queries.materials
      if (!query || !this.pollingIntervalMs) return
      query.stopPolling()
      this.pollingIntervalMs = null
    },

    /**
     * Stop polling a tab nobody is looking at, and catch up on return.
     *
     * A background tab that keeps polling every ten seconds is pure load, but
     * coming back to a stale screen is the very complaint this fixes — so the
     * return path reads the server immediately rather than waiting a tick.
     */
    onVisibilityChange () {
      this.isDocumentHidden = document.hidden
      if (!document.hidden) this.refetchMaterials()
      this.syncPolling()
    },

    refetchMaterials () {
      const query = this.$apollo.queries.materials
      if (!query || !this.company?.id) return
      query.refetch().catch(() => {
        // A failed refresh is not fatal: the poll loop and the bounded window
        // still apply, so the screen recovers on the next tick instead of
        // leaving the user with an error they cannot act on.
      })
    },

    onMaterialWorkStarted ({ companyId } = {}) {
      if (companyId && companyId !== this.company?.id) return
      this.restartObservation()
    },

    onCompanyStateUpdated ({ companyId } = {}) {
      if (companyId && companyId !== this.company?.id) return
      this.$emit('refetch-company')
      this.restartObservation()
    },

    showExportImplementationData () {
      this.$modal.show(
        ExportImplementationDataModal,
        {
          companyId: this.company.id
        },
        { height: 'auto' }
      )
    },

    showCustomUrlsModal () {
      this.$modal.show(
        CustomUrlsModal,
        {
          landingpages: this.company.landingpages
        },
        { height: 'auto' }
      )
    },

    showVariantUpdateModal () {
      this.$modal.show(
        UpdateVariantModal,
        {
          companyId: this.company.id,
          variant: this.company.variant
        },
        { height: 'auto' }
      )
    },

    showLandingpageBulkCreateModal () {
      this.$modal.show(
        BulkCreateLandingpageModal,
        {
          companyId: this.company.id,
          landingpages: this.company.landingpages,
          variant: this.company.variant
        },
        {
          height: 'auto'
        }
      )
    },

    showGenerateModal () {
      this.$modal.show(GenerateModal, {
        companyId: this.company.id,
        materials: this.materials
      })
    }
  }
}
</script>
